import { useCallback, useEffect, useMemo, useState } from 'react';
import wretch from 'wretch';
import formData from 'wretch/addons/formData';
import queryString from 'wretch/addons/queryString';

import { handleError } from 'services/ErrorManagement';

/** Singleton for base wretcher. Bearer token is added on login. */
export const fmhAPI = {
    base: wretch(process.env.REACT_APP_BASE_URL).addon([formData, queryString]),
};

/** Gets FMH token using Auth0 access token */
export const getFmhToken = ({ accessToken }) =>
    fmhAPI.base.auth(`Bearer ${accessToken}`).url('/authorization/access_tokens/').get().json();

export const get = (url, query = undefined) =>
    fmhAPI.base
        .url(url)
        .query(query)
        .get()
        .json((json) => json);

export const useGetWithRefetch = (url, query = undefined) => {
    const [result, setResult] = useState();

    const fetch = useCallback(async () => {
        if (url) {
            try {
                setResult(await get(url, query));
            } catch (err) {
                handleError('Error fetching data', err);
            }
        }
    }, [url, query]);

    useEffect(() => {
        void (async () => {
            await fetch();
        })();
    }, [fetch]);
    return { result, refetch: fetch };
};

// Simplify change to include refetch so we can update where needed
export const useGet = (url, query = undefined) => {
    const { result } = useGetWithRefetch(url, query);
    return result;
};

export const post = (url, body) =>
    fmhAPI.base
        .url(url)
        .post(body)
        .json((json) => json);

export const put = (url, body, query = {}) =>
    fmhAPI.base
        .url(url)
        .query(query)
        .put(body)
        .json((json) => json);

export const patch = (url, body) =>
    fmhAPI.base
        .url(url)
        .patch(body)
        .json((json) => json);

export const httpDelete = (url) =>
    fmhAPI.base
        .url(url)
        .delete()
        .json((json) => json);

export const getFile = (url) => fmhAPI.base.url(url).get().blob();

export const postFile = (url, file, query = undefined, headers = undefined) =>
    fmhAPI.base.url(url).headers(headers).query(query).formData({ file }).post().json();

export const postCustomFormData = (url, formDataBody) =>
    fmhAPI.base.url(url).body(formDataBody).post().json();

/**
 * React hook to fetch SSO domains with optional user type filtering
 * @param {string} [userType] - Optional user type to filter domains: 'supplier', 'prescriber', 'payer', 'lighthouse', 'compass'
 * @returns {string[]|undefined} Array of domain strings or undefined while loading
 */
export const useSSODomains = (userType) => {
    const query = useMemo(() => (userType ? { user_type: userType } : undefined), [userType]);
    return useGet('/authorization/sso_domains/', query);
};
